import { useEffect, useState } from 'react'
import { api, type Release } from '../../lib/api'
import { loadStates, type LoadState } from '../../lib/loadstates'
import { CVButton } from '../../lib/sol/inputs/CVButton'

function formatSize (bytes: number): string {
	if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
	if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
	if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${bytes} B`
}

export function ReleasesPage ({ onSignOut }: { onSignOut: () => void }) {
	const [state, setState] = useState<LoadState<Release[]>>(loadStates.init())

	useEffect(() => {
		const run = async () => {
			const result = await api.fetchReleases()
			setState(loadStates.fromResult(result))
		}
		run()
	}, [])

	const signOut = async () => {
		await api.logout()
		onSignOut()
	}

	if (state.loaded === null) {
		return <div className="min-h-screen w-full flex items-center justify-center relative z-10 text-white/90 text-lg">Loading releases…</div>
	}
	if (state.loaded === false) {
		return <div className="min-h-screen w-full flex items-center justify-center relative z-10 text-danger-200 text-lg">Failed to load releases: {state.error}</div>
	}

	const releases = state.data

	return (
		<div className="min-h-screen w-full flex flex-col items-center relative z-10 px-4 py-6 gap-4">
			<div className="w-full max-w-225 flex items-end justify-between px-1">
				<div>
					<h1 className="text-4xl font-title text-cv-brown-1 text-shadow-sm">Crossing Valley</h1>
					<p className="text-cv-brown-3 text-lg -mb-1 pl-0.5 text-shadow-sm">Game Downloads</p>
				</div>
				<CVButton onClick={signOut}>Sign out</CVButton>
			</div>
			<div className="w-full max-w-225 flex-1 flex flex-col bg-white/85 backdrop-blur-sm rounded-2xl shadow-2xl shadow-black/30 overflow-y-auto p-6">
				{releases.length === 0 && (
					<div className="text-neutral-600 text-lg text-center mt-12">No releases available yet.</div>
				)}
				<div className="flex flex-col gap-5">
					{releases.map((rel) => (
						<div key={rel.name} className="bg-white/70 border border-neutral-200 rounded-xl p-5 shadow-md shadow-black/10">
							<h2 className="text-2xl font-title text-neutral-800 mb-3">{rel.name}</h2>
							{rel.patchNotes !== null && (
								<p className="whitespace-pre-wrap text-sm text-neutral-600 py-1 mb-4 max-h-72 overflow-auto">{rel.patchNotes}</p>
							)}
							<div className="flex flex-col gap-2">
								{rel.variants.map((v) => (
									<div key={v.name} className="flex items-center justify-between bg-black/5 rounded-lg px-4 py-3">
										<div className="flex flex-col">
											<span className="font-medium text-neutral-800">{v.name}</span>
											<span className="text-neutral-500 text-sm">{formatSize(v.sizeBytes)}</span>
										</div>
										<CVButton onClick={() => api.downloadVariant(rel.name, v.name)}>Download</CVButton>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}